import { supabase } from "@/integrations/supabase/client";

// Error types for better client-side handling
export type MeterErrorType =
  | 'OFFLINE'
  | 'TIMEOUT'
  | 'CONNECTION_ERROR'
  | 'NOT_FOUND'
  | 'AUTHENTICATION_FAILED'
  | 'METER_BUSY'
  | 'COMMAND_REJECTED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export interface MeterError {
  type: MeterErrorType;
  message: string;
  details?: string;
}

// Helper to classify error messages
const classifyError = (errorMsg: string): MeterError => {
  const msg = errorMsg.toLowerCase();

  if (msg.includes('timeout') || msg.includes('timed out') || msg.includes('no response')) {
    return {
      type: 'TIMEOUT',
      message: 'Meter did not respond',
      details: 'The meter may be offline or experiencing network issues. Please try again.'
    };
  }

  if (msg.includes('offline') || msg.includes('not connected') || msg.includes('unreachable')) {
    return {
      type: 'OFFLINE',
      message: 'Meter is offline',
      details: 'Check the meter\'s network connection and try again later.'
    };
  }

  if (msg.includes('mqtt') || msg.includes('connack') || msg.includes('broker') || msg.includes('connection')) {
    return {
      type: 'CONNECTION_ERROR',
      message: 'Communication error',
      details: 'Unable to connect to the meter communication system.'
    };
  }

  if (msg.includes('not found') || msg.includes('no meter') || msg.includes('invalid meter')) {
    return {
      type: 'NOT_FOUND',
      message: 'Meter not found',
      details: 'The meter could not be found. Please verify the meter configuration.'
    };
  }

  if (msg.includes('auth') || msg.includes('password') || msg.includes('unauthorized') || msg.includes('rejected')) {
    return {
      type: 'AUTHENTICATION_FAILED',
      message: 'Authentication failed',
      details: 'Failed to authenticate with the meter. Check credentials.'
    };
  }

  if (msg.includes('busy') || msg.includes('in use') || msg.includes('processing')) {
    return {
      type: 'METER_BUSY',
      message: 'Meter is busy',
      details: 'The meter is processing another request. Please wait and try again.'
    };
  }

  if (msg.includes('failed') || msg.includes('error') || msg.includes('d1')) {
    return {
      type: 'COMMAND_REJECTED',
      message: 'Command rejected by meter',
      details: errorMsg
    };
  }

  return {
    type: 'UNKNOWN',
    message: 'Operation failed',
    details: errorMsg
  };
};

// When supabase.functions.invoke() gets a non-2xx response, the SDK throws
// FunctionsHttpError BEFORE reading the response body at all (see
// @supabase/functions-js FunctionsClient: `if (!response.ok) throw new
// FunctionsHttpError(response)` happens before the response.json()/.text()
// parsing step) - so `data` is ALWAYS null on error, and `error.context` is
// the raw, UNREAD Response object, not a pre-parsed body. The actual reason
// (timeout/offline/rejected/etc.) that classifyError needs has to be read
// from error.context asynchronously (.text(), then try JSON) - checking
// error.context.body directly (a ReadableStream, not a string) never works.
export const extractInvokeErrorMessage = async (
  error: { message?: string; context?: { text?: () => Promise<string> } },
  fallback: string
): Promise<string> => {
  let msg = error.message || fallback;

  const ctx = error.context;
  if (ctx && typeof ctx.text === 'function') {
    try {
      const text = await ctx.text();
      if (text) {
        try {
          const body = JSON.parse(text);
          if (body?.message) msg = body.message;
          else if (body?.error) msg = typeof body.error === 'string' ? body.error : JSON.stringify(body.error);
        } catch {
          // Not JSON - use the raw text if it's non-empty.
          if (text.trim()) msg = text;
        }
      }
    } catch {
      // Body already consumed or unreadable - keep whatever msg already holds.
    }
  }

  return msg;
};

export interface MeterControlCommand {
  id: string;
  meter_id: string;
  command_type: 'turn_on' | 'turn_off';
  payload: string;
  mqtt_topic: string;
  status: 'pending' | 'sent' | 'success' | 'failed';
  error_message: string | null;
  sent_at: string | null;
  created_at: string;
  created_by: string | null;
}

export interface MeterReadData {
  meterNumber: string;
  electricityBalance: number;
  combinedActiveTotal: number;
  overdrawnBalance: number;
  // Single-phase fields
  voltageA?: number;
  currentA?: number;
  activePower: number;
  gridFrequency?: number;
  powerFactor?: number;
  statusBits: string;
  // Three-phase fields
  forwardEnergy?: number;
  reverseEnergy?: number;
  phaseAVoltage?: number;
  phaseBVoltage?: number;
  phaseCVoltage?: number;
  phaseACurrent?: number;
  phaseBCurrent?: number;
  phaseCCurrent?: number;
  phaseAActivePower?: number;
  phaseBActivePower?: number;
  phaseCActivePower?: number;
  phaseAPowerFactor?: number;
  phaseBPowerFactor?: number;
  phaseCPowerFactor?: number;
}

// DLT645 encoding functions
const reverseMeterNo = (num: string): string =>
  num.match(/../g)!.reverse().map(b => b.split('').join('')).join('');

const encodeDlt645 = (hex: string): string =>
  hex.match(/../g)!.reverse().map(byte =>
    (parseInt(byte, 16) + 0x33).toString(16).padStart(2, '0')
  ).join('');

const calculateChecksum = (frameBodyHex: string): string => {
  const bytes: string[] = frameBodyHex.match(/../g) || [];
  let sum = 0;
  for (const byte of bytes) {
    sum += parseInt(byte, 16);
  }
  return (sum % 256).toString(16).toUpperCase().padStart(2, '0');
};

const PREAMBLE = 'FEFEFEFE';
const START_OF_FRAME = '68';
const END_SYMBOL = '16';

export class MeterControlService {
  /**
   * Turn a meter on
   */
  static async turnMeterOn(meterId: string): Promise<{ success: boolean; message: string; response?: any }> {
    return await this.sendControlCommand(meterId, 'turn_on');
  }

  /**
   * Turn a meter off
   */
  static async turnMeterOff(meterId: string): Promise<{ success: boolean; message: string; response?: any }> {
    return await this.sendControlCommand(meterId, 'turn_off');
  }

  /**
   * Set overdraft limit on the physical meter (Section 2.9)
   */
  static async setOverdraftLimit(meterId: string, limitKwh: number): Promise<{ success: boolean; message: string; rejected?: boolean }> {
    const { data, error } = await supabase.functions.invoke('mqtt-meter-control', {
      body: { meterId, action: 'set_overdraft', overdraftLimitKwh: limitKwh },
    });
    if (error) return { success: false, message: await extractInvokeErrorMessage(error, 'Failed to send overdraft command') };
    if (data?.success) return { success: true, message: `Overdraft limit set to ${limitKwh} kWh on meter` };
    // Distinguish meter rejection (D4H) from timeout/unreachable
    const rejected = data?.response?.status === 'nack';
    return { success: false, message: data?.error || 'Meter rejected the command', rejected };
  }

  /**
   * Read meter data (balance, consumption, electrical parameters)
   */
  static async readMeter(meterId: string): Promise<{
    success: boolean;
    message: string;
    data?: MeterReadData;
    error?: MeterError;
    extraResponses?: Array<{ meterNumber: string; payload: string; decoded: any }>;
  }> {
    // Get meter details including meter_type
    const { data: meter, error: meterError } = await supabase
      .from('smart_meters')
      .select('meter_number, pkey, is_online, last_reading_at, meter_type')
      .eq('id', meterId)
      .single();

    if (meterError || !meter) {
      const err = classifyError('Meter not found in database');
      return { success: false, message: err.message, error: err };
    }

    if (!meter.meter_number) {
      const err = classifyError('Meter number not configured');
      return { success: false, message: err.message, error: err };
    }

    // Check if meter appears offline (last reading > 30 mins ago)
    const isLikelyOffline = !meter.last_reading_at ||
      (new Date().getTime() - new Date(meter.last_reading_at).getTime()) > 30 * 60 * 1000;

    const meterNumber = meter.meter_number;
    const pKey = meter.pkey || 'TW0003';
    const isThreePhase = meter.meter_type === 'three-phase';

    // Build read command payload
    // Data ID: 04602001 for single-phase, 04602004 for three-phase
    const controlCode = '11';
    const dataLength = '04';
    const dataIdentification = isThreePhase ? '04602004' : '04602001';
    const encodedDataId = encodeDlt645(dataIdentification);

    console.log(`[MeterControl] Reading ${isThreePhase ? 'three-phase' : 'single-phase'} meter with Data ID: ${dataIdentification}`);

    const reverseMeterNum = reverseMeterNo(meterNumber);
    const bodyPayload =
      START_OF_FRAME +
      reverseMeterNum +
      START_OF_FRAME +
      controlCode +
      dataLength +
      encodedDataId;

    const checksum = calculateChecksum(bodyPayload);
    const payload = (PREAMBLE + bodyPayload + checksum + END_SYMBOL).toUpperCase();
    const mqttTopic = `/device/electricmeter/${pKey}/${meterNumber}/data/down`;

    try {
      const { data, error } = await supabase.functions.invoke('mqtt-meter-control', {
        body: {
          command: 'read_meter',
          meterNumber,
          payload,
          topic: mqttTopic
        }
      });

      // Handle Supabase function invoke error (the response body has the real
      // reason even when a non-2xx status is returned).
      if (error) {
        const errorMsg = await extractInvokeErrorMessage(error, 'Failed to communicate with meter service');
        const err = classifyError(errorMsg);
        return { success: false, message: err.message, error: err };
      }

      // Handle success response
      if (data?.success && data?.decoded?.data) {
        return {
          success: true,
          message: 'Meter read successful',
          data: data.decoded.data as MeterReadData,
          extraResponses: data.extraResponses || [],
        };
      }

      // Handle error in response body (200 but success: false)
      const extraResponses = data?.extraResponses || [];
      const errorMessage = data?.message || data?.decoded?.error || data?.error || 'Failed to read meter data';

      // If meter was likely offline and we got an error, provide better context
      if (isLikelyOffline && (errorMessage.includes('timeout') || errorMessage.includes('no response'))) {
        const err: MeterError = {
          type: 'OFFLINE',
          message: 'Meter is offline',
          details: 'The meter has not reported in over 30 minutes and is not responding to commands.'
        };
        return { success: false, message: err.message, error: err, extraResponses };
      }

      const err = classifyError(errorMessage);
      return { success: false, message: err.message, error: err, extraResponses };

    } catch (networkError: any) {
      const err = classifyError(networkError?.message || 'Network error');
      return { success: false, message: err.message, error: err };
    }
  }

  /**
   * Recharge/top-up meter with kWh amount
   * @param meterId - The meter ID to recharge
   * @param kwhAmount - Amount in kWh to add
   * @param options - Optional: adminPayerId to record admin payment, pricePerKwh for TSh calculation
   */
  static async rechargeMeter(
    meterId: string,
    kwhAmount: number,
    options?: { adminPayerId?: string; pricePerKwh?: number; category?: string; reason?: string; description?: string; organizationId?: string }
  ): Promise<{ success: boolean; message: string; newBalance?: number; commandId?: string }> {
    if (kwhAmount <= 0) {
      throw new Error('Amount must be greater than 0');
    }

    if (kwhAmount > 9999.99) {
      throw new Error('Amount exceeds maximum of 9999.99 kWh');
    }

    // Generate unique order number (used for user_topups reference)
    const orderNumber = Date.now().toString().slice(-8);

    // Use server-side action path: edge function handles DLT645 encoding, meter_type, and DB records
    const { data, error } = await supabase.functions.invoke('mqtt-meter-control', {
      body: { action: 'recharge', meterId, amount: kwhAmount, orderNumber }
    });

    if (error) {
      throw new Error(await extractInvokeErrorMessage(error, 'Failed to recharge meter'));
    }

    if (data?.success) {
      // Try to extract new balance from response (action path returns response.data)
      let newBalance: number | undefined;
      if (data.response?.data?.electricityBalance) {
        const balanceHex = data.response.data.electricityBalance;
        newBalance = parseInt(balanceHex, 16) / 100;
      }

      // Record admin payment in user_topups if adminPayerId provided
      if (options?.adminPayerId) {
        try {
          const { data: meterData } = await supabase
            .from('smart_meters')
            .select('apartment_id, building_id')
            .eq('id', meterId)
            .single();

          // Resolve a tenant for the meter's apartment, if any.
          let userId: string | undefined;
          let organizationId: string | undefined;
          if (meterData?.apartment_id) {
            const { data: apartmentUser } = await supabase
              .from('users')
              .select('id, organization_id')
              .eq('apartment_id', meterData.apartment_id)
              .limit(1)
              .maybeSingle();
            userId = apartmentUser?.id;
            organizationId = apartmentUser?.organization_id;
          }
          // Fallbacks so every admin reload is recorded — even on a tenant-less
          // meter — so it reaches the bill report (this was the gap that left
          // manual reloads out of user_topups entirely).
          if (!organizationId && meterData?.building_id) {
            const { data: bld } = await supabase
              .from('buildings').select('organization_id').eq('id', meterData.building_id).maybeSingle();
            organizationId = bld?.organization_id ?? undefined;
          }
          organizationId = organizationId || options.organizationId;
          userId = userId || options.adminPayerId;

          if (userId && organizationId) {
            const pricePerKwh = options.pricePerKwh || 400;
            const amountTsh = Math.round(kwhAmount * pricePerKwh);

            await supabase.from('user_topups').insert({
              user_id: userId,
              organization_id: organizationId,
              amount: amountTsh,
              currency: 'TZS',
              payment_method: 'admin_topup',
              status: 'completed',
              reference: `ADMIN-${orderNumber}`,
              admin_payer_id: options.adminPayerId,
              rate_per_kwh: pricePerKwh,
              completed_at: new Date().toISOString(),
              meter_command_id: data.commandId || null,
              // Bill-report tagging. Admin manual loads default to 'cash'
              // (cash received) when no category is supplied by the caller.
              category: options.category || 'cash',
              topup_reason: options.reason || null,
              description: options.description || null,
            });
          } else {
            console.log('Could not resolve org for admin reload; skipping user_topups record');
          }
        } catch (recordError) {
          console.error('Failed to record admin payment:', recordError);
          // Don't fail the recharge if recording fails
        }
      }

      return {
        success: true,
        message: `Successfully recharged ${kwhAmount.toFixed(2)} kWh`,
        newBalance,
        commandId: data.commandId,
      };
    }

    return {
      success: false,
      message: data?.error || data?.message || 'Recharge failed',
    };
  }

  /**
   * Zero/clear the meter balance by sending a negative recharge equal to the current balance
   * @param meterId - The meter ID to zero
   */
  static async zeroMeterBalance(
    meterId: string
  ): Promise<{ success: boolean; message: string; previousBalance?: number }> {
    // First read the meter to get actual balance
    const readResult = await this.readMeter(meterId);
    if (!readResult.success || !readResult.data) {
      return {
        success: false,
        message: `Cannot zero meter: failed to read current balance - ${readResult.message || 'meter unreachable'}`,
      };
    }

    const currentBalance = readResult.data.electricityBalance;
    if (currentBalance <= 0) {
      return {
        success: true,
        message: 'Meter balance is already at 0',
        previousBalance: currentBalance,
      };
    }

    // Zero via the server-side action path: the edge function reads the meter
    // secrets (service role) and builds the negative-recharge frame, so the
    // browser never needs password/operator_code (locked down for client roles).
    const orderNumber = Date.now().toString().slice(-8);
    const { data, error } = await supabase.functions.invoke('mqtt-meter-control', {
      body: { action: 'zero_balance', meterId, amount: currentBalance, orderNumber },
    });

    if (error) {
      return { success: false, message: await extractInvokeErrorMessage(error, 'Failed to zero meter') };
    }

    if (data?.success) {
      // Update DB
      await supabase.from('smart_meters').update({
        current_reading: 0,
        reload_credit: 0,
        updated_at: new Date().toISOString(),
      }).eq('id', meterId);

      return {
        success: true,
        message: `Meter balance zeroed (was ${currentBalance.toFixed(2)} kWh)`,
        previousBalance: currentBalance,
      };
    }

    return {
      success: false,
      message: data?.message || data?.decoded?.error || 'Zero meter failed',
    };
  }

  /**
   * Send a control command to the meter
   */
  private static async sendControlCommand(
    meterId: string,
    action: 'turn_on' | 'turn_off'
  ): Promise<{ success: boolean; message: string; response?: any; error?: MeterError }> {
    try {
      // First check meter status
      const { data: meter, error: meterError } = await supabase
        .from('smart_meters')
        .select('meter_number, is_online, last_reading_at')
        .eq('id', meterId)
        .single();

      if (meterError || !meter) {
        const err = classifyError('Meter not found in database');
        return { success: false, message: err.message, error: err };
      }

      const { data, error } = await supabase.functions.invoke('mqtt-meter-control', {
        body: { meterId, action },
      });

      // Handle Supabase function invoke error (the response body has the real
      // reason even when a non-2xx status is returned).
      if (error) {
        const errorMsg = await extractInvokeErrorMessage(error, `Failed to send ${action} command`);
        const err = classifyError(errorMsg);
        return { success: false, message: err.message, error: err };
      }

      // Handle success
      if (data?.success) {
        return {
          success: true,
          message: data.message || `Command ${action} sent successfully`,
          response: data.response,
        };
      }

      // Handle error in response body
      const errorMessage = data?.error || data?.message || 'Unknown error occurred';

      // Check if meter is likely offline
      const isLikelyOffline = !meter.last_reading_at ||
        (new Date().getTime() - new Date(meter.last_reading_at).getTime()) > 30 * 60 * 1000;

      if (isLikelyOffline && (errorMessage.includes('timeout') || errorMessage.includes('no response'))) {
        const err: MeterError = {
          type: 'OFFLINE',
          message: 'Meter is offline',
          details: `Cannot ${action === 'turn_on' ? 'turn on' : 'turn off'} - meter is not responding.`
        };
        return { success: false, message: err.message, error: err };
      }

      const err = classifyError(errorMessage);
      return { success: false, message: err.message, error: err };

    } catch (networkError: any) {
      const err = classifyError(networkError?.message || 'Network error');
      return { success: false, message: err.message, error: err };
    }
  }

  /**
   * Get command history for a specific meter
   */
  static async getCommandHistory(
    meterId: string,
    limit: number = 10
  ): Promise<MeterControlCommand[]> {
    const { data, error } = await supabase
      .from('meter_control_commands')
      .select('*')
      .eq('meter_id', meterId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to fetch command history: ${error.message}`);
    }

    return (data || []) as MeterControlCommand[];
  }

  /**
   * Get the current meter status based on latest successful command
   */
  static async getMeterStatus(meterId: string): Promise<'on' | 'off' | 'unknown'> {
    const { data, error } = await supabase
      .from('meter_control_commands')
      .select('command_type, status')
      .eq('meter_id', meterId)
      .eq('status', 'success')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      return 'unknown';
    }

    return data.command_type === 'turn_on' ? 'on' : 'off';
  }

  /**
   * Get real-time meter status including last command and readings
   */
  static async getMeterRealTimeStatus(meterId: string): Promise<{
    isOnline: boolean;
    lastCommand: 'turn_on' | 'turn_off' | null;
    lastReading: number | null;
    lastReadingAt: string | null;
  }> {
    const { data: meter, error } = await supabase
      .from('smart_meters')
      .select('is_online, current_reading, last_reading_at')
      .eq('id', meterId)
      .single();

    if (error) throw new Error(`Failed to fetch meter status: ${error.message}`);

    const lastCommand = await this.getMeterStatus(meterId);
    
    // Map getMeterStatus result to expected type
    let mappedCommand: 'turn_on' | 'turn_off' | null = null;
    if (lastCommand === 'on') {
      mappedCommand = 'turn_on';
    } else if (lastCommand === 'off') {
      mappedCommand = 'turn_off';
    }

    return {
      isOnline: meter.is_online,
      lastCommand: mappedCommand,
      lastReading: meter.current_reading,
      lastReadingAt: meter.last_reading_at,
    };
  }

  /**
   * Control multiple meters at once
   */
  static async batchControl(
    meterIds: string[],
    action: 'turn_on' | 'turn_off'
  ): Promise<{ success: string[]; failed: string[] }> {
    const results = await Promise.allSettled(
      meterIds.map((id) => this.sendControlCommand(id, action))
    );

    const success: string[] = [];
    const failed: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        success.push(meterIds[index]);
      } else {
        failed.push(meterIds[index]);
      }
    });

    return { success, failed };
  }
}
